// src/pages/Contact.jsx
import React, { useState, useEffect } from "react";
import {
  Send,
  CheckCircle2,
  XCircle,
  Mail,
  MapPin,
  Phone,
  Github,
  LinkedinIcon,
} from "lucide-react";
import emailjs from "emailjs-com";
import HeroImage from "../../assets/heroimage.jpg";
export default function ContactForm() {
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [heroVisible, setHeroVisible] = useState(false);
  const [pageVisible, setPageVisible] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setHeroVisible(true), 100);
    const t2 = setTimeout(() => setPageVisible(true), 50);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  useEffect(() => {
    if (status.type === "success") {
      const timer = setTimeout(
        () => setStatus({ type: "", message: "" }),
        5000
      );
      return () => clearTimeout(timer);
    }
  }, [status]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormState((prev) => ({ ...prev, [id]: value }));
    if (status.type === "error") setStatus({ type: "", message: "" });
  };

  const validateForm = () => {
    if (!formState.name.trim()) {
      setStatus({ type: "error", message: "Name is required" });
      return false;
    }
    if (formState.name.length > 100) {
      setStatus({
        type: "error",
        message: "Name must be less than 100 characters",
      });
      return false;
    }
    if (!formState.email.trim()) {
      setStatus({ type: "error", message: "Email is required" });
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formState.email)) {
      setStatus({ type: "error", message: "Please enter a valid email" });
      return false;
    }
    if (formState.email.length > 255) {
      setStatus({
        type: "error",
        message: "Email must be less than 255 characters",
      });
      return false;
    }
    if (!formState.message.trim()) {
      setStatus({ type: "error", message: "Message is required" });
      return false;
    }
    if (formState.message.length > 1000) {
      setStatus({
        type: "error",
        message: "Message must be less than 1000 characters",
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      await emailjs.send(
        serviceId,
        templateId,
        {
          name: formState.name,
          email: formState.email,
          message: formState.message,
        },
        publicKey
      );

      setStatus({ type: "success", message: "Message sent successfully!" });
      setFormState({ name: "", email: "", message: "" });
    } catch (error) {
      console.error("EmailJS error:", error);
      setStatus({
        type: "error",
        message: "Unable to send message. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="bg-white overflow-hidden">
      {/* ── HERO SECTION ── */}
      <div
        className="relative border-b border-gray-100"
        style={{
          backgroundImage: `url(${HeroImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-14">
          <div
            className={`text-center transition-all duration-1000 ease-out ${
              heroVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-8"
            }`}
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 leading-tight tracking-tight">
              Let's <span className="text-blue-400">connect</span>
            </h1>
            <p
              className={`text-gray-200 text-sm sm:text-base md:text-lg leading-relaxed max-w-2xl mx-auto transition-all duration-1000 ease-out delay-150 ${
                heroVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-6"
              }`}
            >
              Whether you're interested in collaboration, have questions about
              my work, or just want to chat about tech I'd love to hear from
              you.
            </p>
          </div>
        </div>
      </div>

      {/* ── CONTENT BELOW HERO ── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-16">
        {/* Get in touch label */}
        <div
          className={`text-center mb-12 transition-all duration-700 ease-out ${
            pageVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-8"
          }`}
        >
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 px-3 py-1 rounded-full">
              <span className="w-1 h-1 rounded-full bg-blue-600"></span>
              <span className="text-blue-700 text-xs font-semibold tracking-wide">
                Get in touch
              </span>
            </div>
          </div>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Contact info & social */}
          <div
            className={`bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-200 transition-all duration-700 ease-out ${
              pageVisible
                ? "opacity-100 translate-x-0"
                : "opacity-0 -translate-x-8"
            }`}
            style={{ transitionDelay: "100ms" }}
          >
            <h2 className="text-2xl font-bold text-black mb-6">
              Contact Information
            </h2>
            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 flex-shrink-0">
                  <Mail size={18} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <a
                    href="mailto:nzilajedidah@gmail.com"
                    className="text-gray-900 hover:text-blue-600 transition-colors"
                  >
                    nzilajedidah@gmail.com
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 flex-shrink-0">
                  <MapPin size={18} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Location</p>
                  <p className="text-gray-900">Nairobi, Kenya</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 flex-shrink-0">
                  <Phone size={18} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <a
                    href="tel:+254769199344"
                    className="text-gray-900 hover:text-blue-600 transition-colors"
                  >
                    +254 769 199 344
                  </a>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 my-6"></div>
            <h3 className="font-semibold text-black mb-4">Connect with me</h3>
            <div className="flex gap-3">
              <a
                href="https://github.com/jedidahnzila"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 hover:bg-blue-600 hover:text-white transition-all"
              >
                <Github size={18} />
              </a>
              <a
                href="https://linkedin.com/in/jedidahnzila"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 hover:bg-blue-600 hover:text-white transition-all"
              >
                <LinkedinIcon size={18} />
              </a>
              <a
                href="mailto:nzilajedidah@gmail.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 hover:bg-blue-600 hover:text-white transition-all"
              >
                <Mail size={18} />
              </a>
            </div>
          </div>

          {/* Right: Contact form */}
          <div
            className={`bg-white rounded-2xl p-6 md:p-8 border border-gray-200 shadow-sm transition-all duration-700 ease-out ${
              pageVisible
                ? "opacity-100 translate-x-0"
                : "opacity-0 translate-x-8"
            }`}
            style={{ transitionDelay: "250ms" }}
          >
            <h2 className="text-2xl font-bold text-black mb-6">
              Send me a message
            </h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              {status.message && (
                <div
                  className={`p-4 rounded-lg flex items-center gap-3 ${
                    status.type === "error"
                      ? "bg-red-50 text-red-700 border border-red-200"
                      : "bg-green-50 text-green-700 border border-green-200"
                  }`}
                >
                  {status.type === "success" ? (
                    <CheckCircle2 size={18} />
                  ) : (
                    <XCircle size={18} />
                  )}
                  <p className="text-sm font-medium">{status.message}</p>
                </div>
              )}
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  value={formState.name}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className="w-full border border-gray-300 rounded-lg p-3 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  value={formState.email}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className="w-full border border-gray-300 rounded-lg p-3 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Message *
                </label>
                <textarea
                  id="message"
                  value={formState.message}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  rows="5"
                  className="w-full border border-gray-300 rounded-lg p-3 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
                  placeholder="What would you like to discuss?"
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-black text-white font-semibold px-6 py-3 rounded-lg hover:bg-blue-600 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <span>{isSubmitting ? "Sending..." : "Send Message"}</span>
                <Send
                  size={18}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </button>
            </form>
          </div>
        </div>
      </div>
    </main>
  );
}
